// Risk Assessment Types and Scoring Logic
// Based on CyberSecurityProtocol 101 Final Specification

export type AssessmentType = 'personal' | 'organization'

// Personal Assessment Answers (6 steps, max 20 points)
export type PersonalAnswers = {
  passwordManager: 'yes' | 'no' // Step 1: 2 points
  mfaEmail: 'yes' | 'no' // Step 1: 2 points
  passwordReuse: 'no' | 'sometimes' | 'yes' // Step 2: 0-2 points
  recoveryOptions: 'yes' | 'not-sure' | 'no' // Step 2: 0-2 points
  deviceEncryption: 'all' | 'some' | 'no' // Step 3: 0-2 points
  autoUpdates: 'automatic' | 'manual' | 'rarely' // Step 3: 0-2 points
  backups: 'cloud-offline' | 'cloud-only' | 'no' // Step 4: 0-2 points
  verifyLinks: 'always' | 'sometimes' | 'rarely' // Step 5: 0-2 points
  breachCheck: 'yes' | 'no' // Step 5: 0-2 points
  incidentAwareness: 'yes' | 'not-confident' | 'no' // Step 6: 0-2 points
}

// Organization Assessment Answers (6 steps, max 18 points)
export type OrganizationAnswers = {
  securityPolicy: 'yes' | 'draft' | 'no' // Step 1: 0-2 points
  mfaEnforced: 'mandatory' | 'partial' | 'no' // Step 2: 0-2 points
  accessReview: 'yes' | 'informal' | 'no' // Step 2: 0-2 points
  deviceManagement: 'yes' | 'partially' | 'no' // Step 3: 0-2 points
  dataClassification: 'yes' | 'partially' | 'no' // Step 4: 0-2 points
  backupTested: 'yes' | 'backup-only' | 'no' // Step 4: 0-2 points
  securityMonitoring: 'yes' | 'limited' | 'no' // Step 5: 0-2 points
  incidentPlan: 'yes' | 'informal' | 'no' // Step 6: 0-2 points
  securityTraining: 'yes' | 'occasionally' | 'no' // Step 6: 0-2 points
}

export type RiskLevel = 'High Risk' | 'Medium Risk' | 'Good' | 'Strong'

export interface AssessmentResult {
  score: number // 0-100
  rawScore: number // Actual points earned
  maxScore: number // Maximum possible points
  level: RiskLevel
  color: string
  summary: string
  priorities: string[]
  roadmap?: {
    day30: string[]
    day60: string[]
    day90: string[]
  }
}

// Personal Assessment Scoring
export function calculatePersonalScore(
  answers: PersonalAnswers,
): AssessmentResult {
  let rawScore = 0
  const maxScore = 20
  const priorities: string[] = []

  // Step 1: Identity & Account Security (4 points)
  if (answers.passwordManager === 'yes') {
    rawScore += 2
  } else {
    priorities.push('Use a password manager')
  }

  if (answers.mfaEmail === 'yes') {
    rawScore += 2
  } else {
    priorities.push('Enable MFA on your primary email')
  }

  // Step 2: Password & Access Hygiene (4 points)
  if (answers.passwordReuse === 'no') {
    rawScore += 2
  } else if (answers.passwordReuse === 'sometimes') {
    rawScore += 1
    priorities.push('Stop reusing passwords across accounts')
  } else {
    priorities.push('Stop reusing passwords across accounts')
  }

  if (answers.recoveryOptions === 'yes') {
    rawScore += 2
  } else if (answers.recoveryOptions === 'not-sure') {
    rawScore += 1
    priorities.push('Review and update account recovery options')
  } else {
    priorities.push('Set up account recovery options')
  }

  // Step 3: Device Security (4 points)
  if (answers.deviceEncryption === 'all') {
    rawScore += 2
  } else if (answers.deviceEncryption === 'some') {
    rawScore += 1
    priorities.push('Enable encryption on all devices')
  } else {
    priorities.push('Enable full disk encryption on all devices')
  }

  if (answers.autoUpdates === 'automatic') {
    rawScore += 2
  } else if (answers.autoUpdates === 'manual') {
    rawScore += 1
    priorities.push('Enable automatic updates')
  } else {
    priorities.push('Enable automatic updates immediately')
  }

  // Step 4: Data Backup & Recovery (2 points)
  if (answers.backups === 'cloud-offline') {
    rawScore += 2
  } else if (answers.backups === 'cloud-only') {
    rawScore += 1
    priorities.push('Add offline backup to your strategy')
  } else {
    priorities.push('Set up cloud and offline backups')
  }

  // Step 5: Digital Habits (4 points)
  if (answers.verifyLinks === 'always') {
    rawScore += 2
  } else if (answers.verifyLinks === 'sometimes') {
    rawScore += 1
    priorities.push('Always verify links before clicking')
  } else {
    priorities.push('Always verify links and attachments')
  }

  if (answers.breachCheck === 'yes') {
    rawScore += 2
  } else {
    priorities.push('Check if your email appears in data breaches')
  }

  // Step 6: Incident Awareness (2 points)
  if (answers.incidentAwareness === 'yes') {
    rawScore += 2
  } else if (answers.incidentAwareness === 'not-confident') {
    rawScore += 1
    priorities.push('Learn basic incident response steps')
  } else {
    priorities.push('Learn what to do if your account is compromised')
  }

  // Convert to 0-100 scale
  const score = Math.round((rawScore / maxScore) * 100)

  // Determine risk level
  let level: RiskLevel
  let color: string
  let summary: string

  if (score >= 85) {
    level = 'Strong'
    color = 'text-emerald-400'
    summary =
      'Excellent! Your personal security posture is strong. Continue maintaining these practices and stay vigilant against emerging threats.'
  } else if (score >= 70) {
    level = 'Good'
    color = 'text-green-400'
    summary =
      'Good work! You have a solid security baseline. Focus on the priority actions below to strengthen your defenses further.'
  } else if (score >= 40) {
    level = 'Medium Risk'
    color = 'text-amber-400'
    summary =
      'Your security has gaps that need attention. Follow the priority actions below to significantly reduce your risk exposure.'
  } else {
    level = 'High Risk'
    color = 'text-red-400'
    summary =
      'Immediate action needed! Your current security posture leaves you vulnerable to common attacks. Start with the top 3 priorities below.'
  }

  // Take top 3 priorities
  const topPriorities = priorities.slice(0, 3)
  if (topPriorities.length === 0) {
    topPriorities.push('Continue monitoring your security posture')
    topPriorities.push('Review your protocols quarterly')
    topPriorities.push('Stay informed about emerging threats')
  }

  return {
    score,
    rawScore,
    maxScore,
    level,
    color,
    summary,
    priorities: topPriorities,
  }
}

// Organization Assessment Scoring
export function calculateOrganizationScore(
  answers: OrganizationAnswers,
): AssessmentResult {
  let rawScore = 0
  const maxScore = 18
  const priorities: string[] = []

  // Step 1: Governance & Policy (2 points)
  if (answers.securityPolicy === 'yes') {
    rawScore += 2
  } else if (answers.securityPolicy === 'draft') {
    rawScore += 1
    priorities.push('Finalize and approve cybersecurity policy')
  } else {
    priorities.push('Create a documented cybersecurity policy')
  }

  // Step 2: Identity & Access Management (4 points)
  if (answers.mfaEnforced === 'mandatory') {
    rawScore += 2
  } else if (answers.mfaEnforced === 'partial') {
    rawScore += 1
    priorities.push('Enforce MFA on all critical accounts')
  } else {
    priorities.push('Implement mandatory MFA for all users')
  }

  if (answers.accessReview === 'yes') {
    rawScore += 2
  } else if (answers.accessReview === 'informal') {
    rawScore += 1
    priorities.push('Formalize access review process')
  } else {
    priorities.push('Implement access review and joiner/mover/leaver process')
  }

  // Step 3: Device & Endpoint Security (2 points)
  if (answers.deviceManagement === 'yes') {
    rawScore += 2
  } else if (answers.deviceManagement === 'partially') {
    rawScore += 1
    priorities.push('Centralize device management for all endpoints')
  } else {
    priorities.push('Implement centralized device management')
  }

  // Step 4: Data Protection & Backup (4 points)
  if (answers.dataClassification === 'yes') {
    rawScore += 2
  } else if (answers.dataClassification === 'partially') {
    rawScore += 1
    priorities.push('Complete data classification and protection')
  } else {
    priorities.push('Classify and protect sensitive data')
  }

  if (answers.backupTested === 'yes') {
    rawScore += 2
  } else if (answers.backupTested === 'backup-only') {
    rawScore += 1
    priorities.push('Test backup restoration regularly')
  } else {
    priorities.push('Implement and test backup strategy')
  }

  // Step 5: Monitoring & Detection (2 points)
  if (answers.securityMonitoring === 'yes') {
    rawScore += 2
  } else if (answers.securityMonitoring === 'limited') {
    rawScore += 1
    priorities.push('Expand security monitoring coverage')
  } else {
    priorities.push('Implement security event monitoring')
  }

  // Step 6: Incident Readiness (4 points)
  if (answers.incidentPlan === 'yes') {
    rawScore += 2
  } else if (answers.incidentPlan === 'informal') {
    rawScore += 1
    priorities.push('Document formal incident response plan')
  } else {
    priorities.push('Create incident response plan')
  }

  if (answers.securityTraining === 'yes') {
    rawScore += 2
  } else if (answers.securityTraining === 'occasionally') {
    rawScore += 1
    priorities.push('Implement regular security awareness training')
  } else {
    priorities.push('Launch security awareness training program')
  }

  // Convert to 0-100 scale
  const score = Math.round((rawScore / maxScore) * 100)

  // Determine risk level
  let level: RiskLevel
  let color: string
  let summary: string

  if (score >= 85) {
    level = 'Strong'
    color = 'text-emerald-400'
    summary =
      'Your organization demonstrates strong cybersecurity governance and controls. Continue refining your program and adapting to emerging threats.'
  } else if (score >= 70) {
    level = 'Good'
    color = 'text-green-400'
    summary =
      'Your organization has a mature security posture with manageable risk. Focus on the key gaps below to reach excellence.'
  } else if (score >= 40) {
    level = 'Medium Risk'
    color = 'text-amber-400'
    summary =
      'Your organization needs improvement in several key areas. The control gaps below represent significant risk exposure that should be addressed systematically.'
  } else {
    level = 'High Risk'
    color = 'text-red-400'
    summary =
      'Critical gaps exist in your security program. Immediate executive attention and resource allocation are needed to address fundamental security controls.'
  }

  // Generate roadmap
  const roadmap = generateOrganizationRoadmap(priorities)

  // Take top 3 priorities for display
  const topPriorities = priorities.slice(0, 3)
  if (topPriorities.length === 0) {
    topPriorities.push('Conduct regular security assessments')
    topPriorities.push('Review and update security policies')
    topPriorities.push('Maintain continuous improvement program')
  }

  return {
    score,
    rawScore,
    maxScore,
    level,
    color,
    summary,
    priorities: topPriorities,
    roadmap,
  }
}

function generateOrganizationRoadmap(priorities: string[]): {
  day30: string[]
  day60: string[]
  day90: string[]
} {
  const day30: string[] = []
  const day60: string[] = []
  const day90: string[] = []

  // Prioritize critical controls for Day 30
  if (priorities.some((p) => p.includes('MFA'))) {
    day30.push('Enable MFA on all critical accounts')
  }
  if (priorities.some((p) => p.includes('backup'))) {
    day30.push('Establish baseline backup strategy')
  }
  if (priorities.some((p) => p.includes('policy'))) {
    day30.push('Draft cybersecurity policy document')
  }

  // Day 60 - Process & Expansion
  if (priorities.some((p) => p.includes('access review'))) {
    day60.push('Implement access review process')
  }
  if (priorities.some((p) => p.includes('device management'))) {
    day60.push('Deploy centralized device management')
  }
  if (priorities.some((p) => p.includes('data classification'))) {
    day60.push('Complete data classification project')
  }

  // Day 90 - Advanced & Governance
  if (priorities.some((p) => p.includes('incident response'))) {
    day90.push('Conduct incident response tabletop exercise')
  }
  if (priorities.some((p) => p.includes('training'))) {
    day90.push('Launch security awareness training program')
  }
  if (priorities.some((p) => p.includes('monitoring'))) {
    day90.push('Implement security monitoring and alerting')
  }

  // Fillers if empty
  if (day30.length === 0) day30.push('Review current security baseline')
  if (day60.length === 0) day60.push('Conduct security control audit')
  if (day90.length === 0) day90.push('Establish quarterly security review')

  return { day30, day60, day90 }
}
